import type { PainelMockUser, PainelRole, PainelUserStatus } from '@/lib/painel-rbac';
import type { SiteStaffRole } from '@/lib/cms-types';
import type { XatOfficialRoleLabel } from '@/lib/xat-official-roles';
import { resolveMediaUrl } from '@/services/api-client';
import { apiDelete, apiGet, apiPost, apiPut } from './api-client';

type ApiUser = {
  id: number | string;
  username: string;
  name: string;
  email: string;
  role?: PainelRole;
  status?: PainelUserStatus;
  official_xat_role?: XatOfficialRoleLabel | null;
  help_center_role?: SiteStaffRole | null;
  avatar_url?: string | null;
  roles?: { name: PainelRole }[];
  created_at?: string;
  updated_at?: string;
  last_login_at?: string;
};

function mapApiUser(row: ApiUser): PainelMockUser {
  const role = row.role ?? row.roles?.[0]?.name ?? 'editor';
  return {
    id: String(row.id),
    username: row.username,
    password: '',
    name: row.name,
    email: row.email,
    role,
    status: row.status ?? 'active',
    lastLoginAt: row.last_login_at ?? row.updated_at ?? new Date().toISOString(),
    createdAt: row.created_at ?? new Date().toISOString(),
    guidesCreated: 0,
    pagesEdited: 0,
    powersEdited: 0,
    officialXatRole: row.official_xat_role ?? null,
    helpCenterRole: (row.help_center_role as SiteStaffRole | null | undefined) ?? null,
    avatarUrl: resolveMediaUrl(row.avatar_url) ?? undefined,
  };
}

export async function getUsers(): Promise<PainelMockUser[]> {
  const res = await apiGet<{ data?: ApiUser[] } | ApiUser[]>('/admin/users', true);
  const rows = Array.isArray(res) ? res : (res.data ?? []);
  return rows.map(mapApiUser);
}

export async function createUser(input: {
  username: string;
  name: string;
  email: string;
  password: string;
  role: PainelRole;
  status?: PainelUserStatus;
  official_xat_role?: XatOfficialRoleLabel | null;
  help_center_role?: SiteStaffRole | null;
}): Promise<PainelMockUser> {
  const res = await apiPost<{ data: ApiUser }>('/admin/users', input, true);
  return mapApiUser(res.data);
}

export async function updateUser(
  id: string,
  patch: Partial<{
    name: string;
    email: string;
    password: string;
    role: PainelRole;
    status: PainelUserStatus;
    official_xat_role: XatOfficialRoleLabel | null;
    help_center_role?: SiteStaffRole | null;
  }>
): Promise<PainelMockUser> {
  const res = await apiPut<{ data: ApiUser }>(`/admin/users/${id}`, patch, true);
  return mapApiUser(res.data);
}

export async function deleteUser(id: string): Promise<void> {
  await apiDelete(`/admin/users/${id}`, true);
}
